import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { INSERT_PLACEHOLDER_COMMAND } from "@sinch/components/form/TextEditor/plugins/PlaceholdersPlugin";
import { useFormMutation } from "@sinch/hooks/useFormMutation";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import type { LexicalEditor } from "lexical";
import { includes, pluck, uniq } from "ramda";
import { useEffect, useMemo, useRef } from "react";
import { MessageFormFields, messageSchema } from "./formSchema";
import { resolveChannelMessage } from "./resolveChannelMessage";
import { SendReconfirmation } from "./queries.graphql";
import { SendReconfirmationMutation, SendReconfirmationMutationVariables } from "./queries.types";
import type { Placeholders } from "./MessageForm";
import { useRecipientsChannelsAvailability } from "./useRecipientsChannelsAvailability";
import { isNotNilOrEmpty } from "ramda-adjunct";

/**
 * Properties for the ReconfirmationDialog component.
 */
interface ReconfirmationDialogProps {
  /**
   * An array of attendance IDs.
   */
  attendanceIds?: {
    id: string;
    userId: string;
  }[];

  /**
   * Callback function to be called upon successful reconfirmation.
   */
  onSuccess: () => void;
  placeholders?: Partial<Record<NotificationChannel, Placeholders>>;
  defaultOpened?: NotificationChannel;
}

interface UseReconfirmationDialogReturn {
  form: ReturnType<typeof useMuiForm<MessageFormFields>>;
  handleSubmit: () => Promise<void>;
  sending: boolean;
}

/**
 * Custom hook to handle the reconfirmation dialog form and submission.
 */
export const useReconfirmationDialog = ({
  attendanceIds,
  onSuccess,
  placeholders = {},
  defaultOpened,
}: ReconfirmationDialogProps): UseReconfirmationDialogReturn => {
  const recipientsAvailability = useRecipientsChannelsAvailability();
  const initRecipients = useMemo(() => uniq(pluck("userId", attendanceIds ?? [])), [attendanceIds]);
  // create instace of form, with or without data
  const form = useMuiForm<MessageFormFields>({
    formOptions: {
      mode: "onChange",
      reValidateMode: "onChange",
    },
    schema: messageSchema({
      initRecipients,
      noRequiredSubject: true,
      recipientsAvailability,
      placeholders,
      defaultOpened,
    }),
  });

  const { handleSubmit, loading } = useFormMutation<
    SendReconfirmationMutation,
    SendReconfirmationMutationVariables,
    MessageFormFields
  >({
    mutation: {
      mutate: SendReconfirmation,
    },
    form,
    variableMapper: ({ message, textMessage, channels, pushMessage }) => {
      const availableRecipients = recipientsAvailability.getAvailableRecipients(
        pluck("userId", attendanceIds ?? []),
        channels
      );

      const availableAttendances = pluck(
        "id",
        (attendanceIds ?? []).filter(({ userId }) => includes(userId, availableRecipients))
      );

      return {
        message: {
          channels: channels,
          message: resolveChannelMessage(channels, { message, textMessage, pushMessage }),
          attendanceIds: availableAttendances,
        },
      };
    },
  });

  useEffect(() => {
    if (isNotNilOrEmpty(initRecipients)) {
      form.reset(messageSchema({ initRecipients, noRequiredSubject: true, defaultOpened }).getDefault());
    }
  }, [initRecipients]);

  const handleSend = async () => {
    try {
      const response = await handleSubmit();
      if (response?.sendReconfirmationMessage.result) {
        onSuccess();
      }
    } catch (e) {
      console.error(e);
      return;
    }
  };

  return {
    form,
    handleSubmit: handleSend,
    sending: loading,
  };
};

export const useCustomPlaceholderInsert = (placeholders: Placeholders) => {
  const ref = useRef<LexicalEditor | null>(null);
  const handleAddUrl = (placeholder: string) => {
    ref.current?.dispatchCommand(INSERT_PLACEHOLDER_COMMAND, { placeholder: placeholder });
  };

  const placeholderAddProps = placeholders.map(({ label, value }) => ({
    children: label,
    onClick: () => handleAddUrl(value),
  }));

  return { ref, placeholderAddProps };
};
